import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/lib/nav";
import type { CSSProperties } from "react";

import EmptyState from "@buildpy/ui/empty-state";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { cn } from "@/lib/utils";
import { listHref } from "@buildpy/core/modules/list/search";
import {
  CALENDAR_WEEKDAYS,
  type CalendarDay,
  type CalendarDayItem,
  type CalendarSegment,
  type CalendarWeek,
  type SiteCalendar as SiteCalendarData,
} from "@buildpy/core/modules/project/site-calendar";
import ProjectStatusBadge from "@/modules/project/ui/status-badge";

// 막대 자리(px) — 날짜 줄 아래부터 한 줄 22px.
const BAR_TOP = 28;
const LANE_PX = 22;

// 현장 톤 → 막대 바탕·왼쪽 선(클래스는 문자열 그대로 — 조립하면 Tailwind 가 못 읽는다).
const TONE_BAR = [
  "bg-accent border-primary",
  "bg-status-new-bg border-status-new",
  "bg-status-done-bg border-status-done",
  "bg-status-progress-bg border-status-progress",
  "bg-status-hold-bg border-status-hold",
] as const;

const TONE_MARK = [
  "border-primary text-primary",
  "border-status-new text-status-new",
  "border-status-done text-status-done",
  "border-status-progress text-status-progress",
  "border-status-hold text-status-hold",
] as const;

const toneBar = (tone: number): string => TONE_BAR[tone % TONE_BAR.length]!;
const toneMark = (tone: number): string => TONE_MARK[tone % TONE_MARK.length]!;

const weekdayText = (weekday: number): string =>
  weekday === 0 ? "text-destructive" : weekday === 6 ? "text-status-new" : "text-muted-foreground";

export type SiteCalendarKeep = Record<string, string | number | null | undefined>;

interface Props {
  calendar: SiteCalendarData;
  // 목록 주소(보통 /app/projects).
  path: string;
  // 그대로 둘 다른 쿼리(view·who 등) — month·day 는 달력이 정한다.
  keep?: SiteCalendarKeep;
}

const hrefOf = (path: string, keep: SiteCalendarKeep, month: string | null, day: string | null): string =>
  listHref(path, { ...keep, page: null, month, day });

const projectHref = (id: string): string => `/app/projects/${id}`;

const Segment = ({ segment: s }: { segment: CalendarSegment }) => (
  <div
    className={cn(
      "pointer-events-none absolute top-(--top) left-(--x) h-[19px] w-(--w) truncate whitespace-nowrap px-1.5 text-xs leading-4.25",
      s.kind === "phase"
        ? cn(
            "rounded-sm text-foreground",
            toneBar(s.tone),
            s.continuesBefore ? "rounded-l-none border-l border-dashed" : "border-l-3",
            s.continuesAfter && "rounded-r-none",
            s.held && "border-dashed text-muted-foreground"
          )
        : cn("rounded-sm border bg-card font-semibold", toneMark(s.tone))
    )}
    data-testid="site-calendar-segment"
    style={
      {
        "--top": `${BAR_TOP + s.lane * LANE_PX}px`,
        "--x": `calc(${(s.col / 7) * 100}% + 3px)`,
        "--w": `calc(${(s.span / 7) * 100}% - 6px)`,
      } as CSSProperties
    }>
    {s.label}
  </div>
);

const DayCell = ({ day, href }: { day: CalendarDay; href: string }) => (
  <Link
    aria-current={day.today ? "date" : undefined}
    aria-label={`${day.title}${day.holiday ? ` ${day.holiday}` : ""}`}
    className={cn(
      "block h-full min-w-0 border-r border-border px-1.5 py-1 last:border-r-0",
      day.selected
        ? "bg-accent ring-2 ring-inset ring-primary"
        : day.inMonth
          ? "bg-card hover:bg-muted"
          : "bg-background hover:bg-muted"
    )}
    data-selected={day.selected || undefined}
    data-testid="site-calendar-day"
    href={href}>
    <span className="flex min-w-0 items-center gap-1">
      <span
        className={cn(
          "inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1 text-xs tabular-nums",
          day.today
            ? "bg-primary font-semibold text-primary-foreground"
            : cn(
                "font-medium",
                !day.inMonth
                  ? "text-muted-foreground"
                  : day.weekday === 0 || day.holiday
                    ? "text-destructive"
                    : day.weekday === 6
                      ? "text-status-new"
                      : "text-foreground"
              )
        )}>
        {day.day === 1 || !day.inMonth ? `${Number(day.date.slice(5, 7))}/${day.day}` : day.day}
      </span>
      {day.holiday && <span className="truncate text-xs text-destructive">{day.holiday}</span>}
    </span>
  </Link>
);

const Week = ({ week, maxLanes, dayHref }: { week: CalendarWeek; maxLanes: number; dayHref: (date: string) => string }) => (
  <div className="relative h-36 shrink-0 border-b border-border last:border-b-0">
    <div className="grid h-full grid-cols-7">
      {week.days.map((day) => (
        <DayCell key={day.date} day={day} href={dayHref(day.date)} />
      ))}
    </div>
    {week.segments.map((s) => (
      <Segment key={s.key} segment={s} />
    ))}
    {week.more.map((m) => (
      <span
        key={m.col}
        className="pointer-events-none absolute top-(--top) left-(--x) text-xs font-medium text-primary"
        style={{ "--top": `${BAR_TOP + maxLanes * LANE_PX + 1}px`, "--x": `calc(${(m.col / 7) * 100}% + 9px)` } as CSSProperties}>
        +{m.count}건
      </span>
    ))}
  </div>
);

const DayItem = ({ item }: { item: CalendarDayItem }) => (
  <Link
    className={cn("flex flex-col gap-0.5 rounded-md border-l-3 px-2.5 py-2 hover:brightness-95", toneBar(item.tone))}
    data-testid="site-calendar-item"
    href={projectHref(item.projectId)}>
    <span className="flex items-center justify-between gap-2">
      <span className="truncate text-sm font-semibold text-foreground">{item.title}</span>
      <span className="shrink-0 text-xs text-muted-foreground">{item.owner}</span>
    </span>
    <span className="text-xs text-muted-foreground">{item.text}</span>
  </Link>
);

const Legend = () => (
  <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
    <li className="flex items-center gap-1.5">
      <span className="h-2.5 w-[18px] rounded-sm border-l-3 border-primary bg-accent" />
      공정
    </li>
    <li className="flex items-center gap-1.5">
      <span className="h-2.5 w-3.5 rounded-sm border border-foreground bg-card" />
      착공·준공
    </li>
  </ul>
);

// 현장 달력 — 한 달 격자에 착공·준공·공정 막대, 오른쪽에 고른 날 일정과 이달 현장. 폰 폭에서는 격자 대신 날짜별 목록.
const SiteCalendar = ({ calendar, path, keep = {} }: Props) => {
  const { selected } = calendar;
  const dayHref = (date: string) => hrefOf(path, keep, calendar.month, date);

  return (
    <div className="flex min-w-0 flex-col gap-3" data-testid="site-calendar">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <nav aria-label="달 이동" className="flex items-center gap-2">
          <Button asChild size="icon" variant="outline">
            <Link aria-label="이전 달" href={hrefOf(path, keep, calendar.prevMonth, null)}>
              <ChevronLeft />
            </Link>
          </Button>
          <h2 className="min-w-[7.25rem] text-center text-lg font-semibold tabular-nums" data-testid="site-calendar-month">
            {calendar.monthTitle}
          </h2>
          <Button asChild size="icon" variant="outline">
            <Link aria-label="다음 달" href={hrefOf(path, keep, calendar.nextMonth, null)}>
              <ChevronRight />
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link href={hrefOf(path, keep, null, null)}>
              오늘
            </Link>
          </Button>
        </nav>
        <Legend />
      </div>

      <div className="flex min-w-0 flex-col gap-4 lg:flex-row">
        <SectionCard
          aria-label={`${calendar.monthTitle} 달력`}
          className="hidden min-w-0 flex-1 flex-col overflow-hidden md:flex"
          padding="none">
          <div className="grid h-8 grid-cols-7 border-b border-border bg-background">
            {CALENDAR_WEEKDAYS.map((w, i) => (
              <div key={w} className={cn("flex items-center justify-center text-xs font-medium", weekdayText(i))}>
                {w}
              </div>
            ))}
          </div>
          {calendar.weeks.map((week) => (
            <Week key={week.days[0]!.date} dayHref={dayHref} maxLanes={calendar.maxLanes} week={week} />
          ))}
        </SectionCard>

        <SectionCard
          aria-label={`${calendar.monthTitle} 일정`}
          className="min-w-0 md:hidden"
          data-testid="site-calendar-agenda"
          padding="md">
          {calendar.agenda.length === 0 ? (
            <EmptyState variant="plain">이달 잡힌 일정이 없습니다</EmptyState>
          ) : (
            <ul className="flex flex-col gap-4">
              {calendar.agenda.map((d) => (
                <li key={d.date} className="flex flex-col gap-2">
                  <h3 className="flex items-baseline gap-2 text-sm font-semibold">
                    <span className={cn(d.today && "text-primary")}>
                      {d.title}
                      {d.today && " · 오늘"}
                    </span>
                    {d.holiday && <span className="text-xs font-normal text-destructive">{d.holiday}</span>}
                  </h3>
                  {d.items.map((item) => (
                    <DayItem key={item.key} item={item} />
                  ))}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <aside className="flex min-w-0 shrink-0 flex-col gap-3 lg:w-72">
          <SectionCard className="hidden flex-col gap-2.5 md:flex" data-testid="site-calendar-selected">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-base font-semibold">
                {selected.title}
                {selected.today && " · 오늘"}
              </h2>
              <span className="shrink-0 text-xs text-muted-foreground">일정 {selected.items.length}건</span>
            </div>
            {selected.holiday && <span className="text-xs text-destructive">{selected.holiday}</span>}
            {selected.items.map((item) => (
              <DayItem key={item.key} item={item} />
            ))}
            {selected.items.length === 0 && <p className="text-sm text-muted-foreground">잡힌 일정이 없습니다</p>}
            {selected.owners.length > 0 && (
              <div className="flex flex-col gap-1.5 border-t border-border pt-2.5">
                <span className="text-xs text-muted-foreground">이날 담당자별 현장</span>
                {selected.owners.map((o) => (
                  <div key={o.name} className="flex items-center gap-2 text-sm">
                    <span className="w-11 shrink-0 truncate">{o.name}</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn("h-full w-(--w)", o.busy ? "bg-warning" : "bg-primary")}
                        style={{ "--w": `${o.ratio * 100}%` } as CSSProperties}
                      />
                    </div>
                    <span
                      className={cn(
                        "w-7 text-right text-xs tabular-nums",
                        o.busy ? "font-semibold text-warning" : "text-muted-foreground"
                      )}>
                      {o.count}곳
                    </span>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard className="flex flex-col gap-1" data-testid="site-calendar-sites">
            <h2 className="mb-0.5 text-sm font-semibold">이달 현장 {calendar.sites.length}곳</h2>
            {calendar.sites.length === 0 && <p className="text-sm text-muted-foreground">이달 잡힌 현장이 없습니다</p>}
            {calendar.sites.map((site) => (
              <Link
                key={site.id}
                className="flex min-h-7 items-center gap-2 rounded-md px-1 text-sm hover:bg-muted"
                href={projectHref(site.id)}>
                <span className={cn("size-2.5 shrink-0 rounded-sm border-2", toneBar(site.tone))} />
                <span className="min-w-0 flex-1 truncate">{site.title}</span>
                <ProjectStatusBadge status={site.status} />
              </Link>
            ))}
          </SectionCard>
        </aside>
      </div>
    </div>
  );
};

export default SiteCalendar;
